import * as React from "react";
import { AutoQuoteSettings, Branch } from "gen/clients";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableColumn, DataTableRowAction } from "components/DataTable/types";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { formatPriceWithCurrency } from "utils/currencyUtils";

interface Props {
  branches: Branch[];
  autoQuoteSettingsByBranchId: Record<string, AutoQuoteSettings>;
  defaultSettings: AutoQuoteSettings;
  onEditBranchSettings: (branch: Branch, currentSettings: AutoQuoteSettings) => void;
  onAddBranchSettings: () => void;
  onDeleteBranchSettings: (branch: Branch, currentSettings: AutoQuoteSettings) => void;
}

const BranchSettingsTable: React.FC<Props> = ({
  branches,
  autoQuoteSettingsByBranchId,
  defaultSettings,
  onEditBranchSettings,
  onAddBranchSettings,
  onDeleteBranchSettings
}) => {
  // Filter branches to only show those with custom settings
  const branchesWithCustomSettings = React.useMemo(() => {
    return branches.filter(branch => autoQuoteSettingsByBranchId[branch.id] !== undefined);
  }, [branches, autoQuoteSettingsByBranchId]);

  const columns: DataTableColumn[] = [
    {
      id: "id",
      title: "Branch ID",
      cell: (branch: Branch) => branch.id
    },
    {
      id: "name",
      title: "Branch Name",
      cell: (branch: Branch) => branch.name
    },
    {
      id: "currency",
      title: "Currency",
      cell: (branch: Branch) => branch.currency || "USD"
    },
    {
      id: "pricePerPage",
      title: "Price Per Page",
      cell: (branch: Branch) => {
        const settings = autoQuoteSettingsByBranchId[branch.id];
        return (
          <Typography variant="body2">{formatPriceWithCurrency(settings.pricePerPage, branch.currency)}</Typography>
        );
      }
    },
    {
      id: "pageCountEscalation",
      title: "Page Count Escalation",
      cell: (branch: Branch) => {
        const settings = autoQuoteSettingsByBranchId[branch.id];
        return <Typography variant="body2">{settings.pageCountEscalationThreshold} pages</Typography>;
      }
    },
    {
      id: "quoteExpiration",
      title: "Quote Expiration",
      cell: (branch: Branch) => {
        const settings = autoQuoteSettingsByBranchId[branch.id];
        return <Typography variant="body2">{settings.quoteExpiration} days</Typography>;
      }
    }
  ];

  const rowActions = React.useCallback(
    (branch: Branch): DataTableRowAction[] => [
      {
        title: "Edit Settings",
        action: () => {
          const currentSettings = autoQuoteSettingsByBranchId[branch.id];
          onEditBranchSettings(branch, currentSettings);
        }
      },
      {
        title: "Delete",
        action: () => {
          const currentSettings = autoQuoteSettingsByBranchId[branch.id];
          onDeleteBranchSettings(branch, currentSettings);
        },
        color: "error.main"
      }
    ],
    [autoQuoteSettingsByBranchId, onEditBranchSettings, onDeleteBranchSettings]
  );

  const availableBranches = branches.filter(branch => autoQuoteSettingsByBranchId[branch.id] === undefined);

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Branch-Specific Auto-Quote Settings</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddBranchSettings}
            disabled={availableBranches.length === 0}
          >
            Add Branch Settings
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure custom auto-quote settings for specific branches. Branches not listed here will use the default
          settings (Price: {formatPriceWithCurrency(defaultSettings.pricePerPage)}, Escalation:{" "}
          {defaultSettings.pageCountEscalationThreshold} pages, Expiration: {defaultSettings.quoteExpiration} days).
        </Typography>

        {branchesWithCustomSettings.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No branch-specific settings configured.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {availableBranches.length === 0
                ? "Add branches in the Branches tab first, then return here to set custom settings."
                : "Click 'Add Branch Settings' to configure custom auto-quote settings for specific branches."}
            </Typography>
          </Box>
        ) : (
          <DataTable
            data={branchesWithCustomSettings}
            columns={columns}
            rowActions={rowActions}
            rowKey={(branch: Branch) => branch.id}
          />
        )}
      </CardContent>
    </Card>
  );
};

export { BranchSettingsTable };
